import {
  MAlert,
  MButton,
  MDialog,
  MIcons,
  MSpinner,
  MTextField,
  errorMsg,
} from "@material-dev/insights-portal-ui-components";
import MAutocomplete from "@material-dev/insights-portal-ui-components/lib/ui-components/m-autocomplete/MAutocomplete";
import { FormikProvider, useFormik } from "formik";
import { SyntheticEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "@store";
import { useStyles } from "./CreateCompositeProjectDialogStyle";
import { tryGetValueFromJson } from "utils/parseHelper";
import {
  projectSelector,
  setAddCompositeProjectDialogOpenedState,
  setOpenCreateCompositeProject,
} from "slices/project/projectSlice";
import {
  CreateCompositeProjectFormModel,
  CreateCompositeProjectFormSchema,
  initialCreateStackForm,
} from "./CreateCompositeProjectSchema";
import { NormalizedROriginProject } from "slices/rOriginProject/rOriginProjectState";
import { getROriginProjects } from "slices/rOriginProject/rOriginProjectSlice";
import { compositeMergeProjects, projectsDataSelector } from "slices/projectsData/projectsDataSlice";
import { resetProjects } from "slices/projects/projectsSlice";
import SelectedLabel from "./SelectedLabel";
import { Badge } from "@mui/material";
import BetaChip from "components/common/BetaChip/BetaChip";
import { LoadingState } from "types";
import { useNavigate } from "react-router";
import { DATASETS_PATH } from "const/routes";
import { resetAddWorkdayProjectDataset, setWorkdayProjectId, setIsAddNewWorkdayDatasetDialogOpenedState } from "slices/workdayProjectDataset/workdayProjectDatasetSlice";

const FAILURE_DETAIL_FIELD = "detail";

type CreateCompositeProjectDialogProps = {
  open: boolean;
  workdayProjectId?: number;
  isWorkdayDataset:boolean;
  onClose?: () => void;
};
const CreateCompositeProjectDialog = memo(({ open, workdayProjectId, isWorkdayDataset, onClose }: CreateCompositeProjectDialogProps) => {
  const { errors } = useAppSelector((state) => state.modificationStacks);
  const { isAddCompositeProjectDialogOpened } = useSelector(projectSelector);
  const { result: validationResult, errors: validationErrors } = useAppSelector(
    (state) => state.modificationStackValidation,
  );
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const { currentUser } = useAppSelector((state) => state.userState);
  const originProjectsState = useAppSelector((state) => state.rOriginProjectSelector);
  const { rOriginProjects, processing: isROriginProjectsLoading } = originProjectsState;
  const [hoveredProject, setHoveredProject] = useState<string | null | undefined>();
  const [hoveredMergedProject, setHoveredMergedProject] = useState<string | null | undefined>();
  const { compoisteProjectMergeStatus, compositeProjectMergeResponse } = useSelector(projectsDataSelector);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (formData: CreateCompositeProjectFormModel) => {
      const appendedProjectIds = formData.mergedProject.map((x) => x.name);
      const hasNewProjects = formData.mergedProject.length > 0;
      if (!hasNewProjects) {
        errorMsg("No new R datasets have been selected to merge. Try again.", {
          closeOnClick: true,
          anchorOrigin: { horizontal: "left", vertical: "top" },
          autoHideDuration: 10000,
        });
        return;
      }
      if (formData.refrenceProject.length > 0 && formData.name) {
        dispatch(
          compositeMergeProjects({
            appendedProjectIds,
            baseProjectId: formData.refrenceProject.map((x) => x.name)[0] ?? "",
            projectName: formData.name,
            workdayProjectId: workdayProjectId
          }),
        );
      }
    },
    [onsubmit, workdayProjectId],
  );

  const formik = useFormik<CreateCompositeProjectFormModel>({
    initialValues: initialCreateStackForm,
    onSubmit: handleSubmit,
    validationSchema: CreateCompositeProjectFormSchema,
  });

  const renderOptions = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: NormalizedROriginProject) => {
      const adornmentValue = option.sourceCode ?? option.source;
      const adornment = adornmentValue ? `(${adornmentValue})` : "";

      // Return the rendered option
      return (
        <div onMouseEnter={() => handleMouseEnter(option.label)} onMouseLeave={() => handleMouseEnter("")}>
          <li
            {...props}
            key={`${option.name}-${option.label ?? ""}-${option.environmentKey ?? ""}-${option.sourceCode ?? ""}`}>
            {option.label ? `${option.name} ${adornment} - ${option.label}` : `${option.name} ${adornment}`}
          </li>
        </div>
      );
    },
    [hoveredProject],
  );

  const renderMergeProjectOptions = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: NormalizedROriginProject) => {
      const adornmentValue = option.sourceCode ?? option.source;
      const adornment = adornmentValue ? `(${adornmentValue})` : "";

      // Return the rendered option
      return (
        <div
          onMouseEnter={() => handleMouseEnterOnMerge(option.label)}
          onMouseLeave={() => handleMouseEnterOnMerge("")}>
          <li
            {...props}
            key={`${option.name}-${option.label ?? ""}-${option.environmentKey ?? ""}-${option.sourceCode ?? ""}`}>
            {option.label ? `${option.name} ${adornment} - ${option.label}` : `${option.name} ${adornment}`}
          </li>
        </div>
      );
    },
    [hoveredMergedProject],
  );
  const handleMouseEnter = useCallback(
    (opt: string | undefined) => {
      setHoveredProject(opt);
    },
    [hoveredProject],
  );

  const handleMouseEnterOnMerge = useCallback(
    (opt: string | undefined) => {
      setHoveredMergedProject(opt);
    },
    [hoveredMergedProject],
  );

  const closeDialog = () => {
    setHoveredProject("");
    setHoveredMergedProject("");
    dispatch(setOpenCreateCompositeProject(false));
    dispatch(resetAddWorkdayProjectDataset);
    if(isWorkdayDataset && onClose){
      onClose();
    }else{
    dispatch(setAddCompositeProjectDialogOpenedState(true));
    }
  };

  const getTitle = useCallback(
    () => (
      <div>
        <MButton className={classes.btnBack} onClick={handleBackClick}>
          <MIcons.ArrowBack className={classes.backIcon} />
          Go back
        </MButton>

        <div className={classes.title}>
          <Badge badgeContent={<BetaChip />} className={classes.betaBadge}>
            Select datasets to merge
          </Badge>
        </div>
      </div>
    ),
    [],
  );

  useEffect(() => {
    formik.resetForm();
    dispatch(getROriginProjects());
  }, [isAddCompositeProjectDialogOpened]);

  useEffect(() => {
    let shouldCloseDialog = false;
    if (compoisteProjectMergeStatus === LoadingState.failed) {
      errorMsg(compositeProjectMergeResponse?.error ?? "", {
        anchorOrigin: { horizontal: "left", vertical: "top" },
        closeOnClick: true,
        autoHideDuration: 10000,
      });
      shouldCloseDialog = true;
    } else if (compoisteProjectMergeStatus === LoadingState.succeed) {
      shouldCloseDialog = true;
    }
    if (shouldCloseDialog) {
      closeDialog();
      dispatch(resetProjects());
      dispatch(setAddCompositeProjectDialogOpenedState(false));
      dispatch(setWorkdayProjectId());     
      if (compositeProjectMergeResponse?.projectId) {
        const url = `${DATASETS_PATH}/${compositeProjectMergeResponse?.projectId}/`;
        navigate(url);
      }
    }
  }, [compoisteProjectMergeStatus, compositeProjectMergeResponse, dispatch]);

  const handleBackClick = () => {
    dispatch(setOpenCreateCompositeProject(false));
    dispatch(resetAddWorkdayProjectDataset);
    if(isWorkdayDataset && onClose){
      onClose();
    }else{
    dispatch(setIsAddNewWorkdayDatasetDialogOpenedState(true));
    if (currentUser?.isAdmin) 
        dispatch(setAddCompositeProjectDialogOpenedState(true));
    else 
        dispatch(setAddCompositeProjectDialogOpenedState(false));
    }
  };

  const errorMessage =
    validationResult?.errors ??
    validationResult?.failures?.map((failure) => tryGetValueFromJson(failure, FAILURE_DETAIL_FIELD))?.join(", ") ??
    errors ??
    validationErrors;

  const handleSelected = useCallback(
    (_: SyntheticEvent<Element, Event>, value: NormalizedROriginProject | null | undefined) => {
      if (value != null) {
        formik.setFieldValue("refrenceProject", [{ name: value.name, label: value.label }]);
      }
    },
    [formik],
  );

  const handleSelectedMergedProject = useCallback(
    (_: SyntheticEvent<Element, Event>, value: NormalizedROriginProject | null | undefined) => {
      if (value != null) {
        formik.setFieldValue("mergedProject", [
          { name: value.name, label: value.label },
          ...formik.values.mergedProject.filter((item) => item.name !== value.name),
        ]);
      }
    },
    [formik],
  );

  const getActions = useMemo(
    () => (
      <div className={classes.actions}>
        <MButton
          color="primary"
          variant="outlined"
          onClick={closeDialog}
          disabled={compoisteProjectMergeStatus === LoadingState.pending}>
          Cancel
        </MButton>
        <MButton
          type="submit"
          color="primary"
          variant="contained"
          form="create-composite-form"
          disabled={compoisteProjectMergeStatus === LoadingState.pending || !formik.isValid}>
          Merge datasets
        </MButton>
      </div>
    ),
    [formik, compoisteProjectMergeStatus],
  );

  return (
    <MDialog
      classes={{
        paper: classes.dialog,
      }}
      titleElement={getTitle()}
      actionsElement={getActions}
      open={open}
      onClose={closeDialog}
      fullWidth>
      <FormikProvider value={formik}>
        <form ref={formRef} id="create-composite-form" className={classes.form} onSubmit={formik.handleSubmit}>
          <div className={classes.root}>
            {errorMessage && (
              <MAlert
                classes={{
                  message: classes.alert,
                }}
                severity="error"
                closable>
                {errorMessage}
              </MAlert>
            )}
            <div className={classes.formItem}>
              <div className={classes.Plabel}>Dataset Name *</div>
              <MTextField
                placeholder="Enter a dataset name that is different than the reference dataset"
                name="name"
                className={classes.input}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                value={formik.values.name}
                helperText={
                  <span style={{ color: "red" }}>
                    {formik.touched.name && formik.errors.name ? formik.errors.name : ""}
                  </span>
                }
                size="small"
                fullWidth
              />
              <span className={classes.nameHelper}></span>
            </div>
            <div className={classes.field}>
              <div className={classes.formItem}>
                <div className={classes.label}>Select a reference dataset *</div>
                <div className={classes.label2}>You can select only 1 reference dataset.</div>

                <MAutocomplete
                  options={rOriginProjects}
                  onChange={(event, value, reason) => {
                    if (reason === "clear") {
                      setHoveredProject("");
                    } else {
                      handleSelected(event, value);
                    }
                  }}
                  loading={isROriginProjectsLoading}
                  defaultValue={null}
                  key={isROriginProjectsLoading ? "project" : "project-loaded"}
                  renderInput={(params) => (
                    <MTextField
                      {...params}
                      name="project"
                      variant="outlined"
                      placeholder={hoveredProject || "Select a dataset"}
                      className={hoveredProject ? classes.blackPlaceholder : classes.grayPlaceholder}
                    />
                  )}
                  renderOption={renderOptions}
                />
                <MSpinner isLoading={isROriginProjectsLoading} size={30} />
                {formik.values.refrenceProject.map((project) => (
                  <SelectedLabel
                    key={project.name}
                    id={project.name}
                    name={project.label ?? ""}
                    onDelete={() => {
                      const updatedProjects = formik.values.refrenceProject.filter((p) => p.name !== project.name);
                      formik.setFieldValue("refrenceProject", updatedProjects);
                      setHoveredProject("");
                    }}
                  />
                ))}
              </div>
              <div className={classes.formItem}>
                <div className={classes.label}>Select dataset(s) to merge *</div>
                <div className={classes.label2}>You can select more then 1 dataset to merge.</div>
                <MAutocomplete
                  options={rOriginProjects}
                  onChange={(event, value, reason) => {
                    if (reason === "clear") {
                      setHoveredMergedProject("");
                    } else {
                      handleSelectedMergedProject(event, value);
                    }
                  }}
                  loading={isROriginProjectsLoading}
                  key={isROriginProjectsLoading ? "project" : "project-loaded"}
                  defaultValue={null}
                  renderInput={(params) => (
                    <MTextField
                      {...params}
                      name="project"
                      variant="outlined"
                      placeholder={hoveredMergedProject || "Select dataset(s)"}
                      className={hoveredMergedProject ? classes.blackPlaceholder : classes.grayPlaceholder}
                    />
                  )}
                  renderOption={renderMergeProjectOptions}
                />
                {formik.values.mergedProject.map((project) => (
                  <SelectedLabel
                    key={project.name}
                    id={project.name}
                    name={project.label ?? ""}
                    onDelete={() => {
                      const updatedProjects = formik.values.mergedProject.filter((p) => p.name !== project.name);
                      formik.setFieldValue("mergedProject", updatedProjects);
                      setHoveredMergedProject("");
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </form>
      </FormikProvider>
    </MDialog>
  );
});
export default CreateCompositeProjectDialog;
